import { useTranslation } from 'react-i18next'
import type { z } from 'zod'

import {
  conditionSchema,
  conditionOperatorSchema,
} from '@/schemas/workflow-node'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

type Condition = z.infer<typeof conditionSchema>
type ConditionOperator = z.infer<typeof conditionOperatorSchema>
type ConditionOperand = Condition['left']

interface ConditionBuilderProps {
  value: Condition | null
  onChange: (value: Condition | null) => void
  upstreamNodes: {
    id: string
    label: string
    output_schema: { name: string; type: string }[]
  }[]
}

const ALL_OPERATORS = conditionOperatorSchema.options
const UNARY_OPERATORS: ConditionOperator[] = ['is_empty', 'is_not_empty']

const DEFAULT_OPERAND: ConditionOperand = { kind: 'literal', value: '' }
const DEFAULT_CONDITION: Condition = {
  operator: 'equals',
  left: { kind: 'variable', ref: '' },
  right: DEFAULT_OPERAND,
}

function OperandEditor({
  value,
  onChange,
  label,
  upstreamNodes,
}: {
  value: ConditionOperand
  onChange: (operand: ConditionOperand) => void
  label: string
  upstreamNodes: ConditionBuilderProps['upstreamNodes']
}) {
  const kind = value.kind

  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <div className="flex items-center gap-2">
        {/* Kind selector */}
        <Select
          value={kind}
          onValueChange={(v) => {
            if (v === 'variable') {
              onChange({ kind: 'variable', ref: '' })
            } else {
              onChange({ kind: 'literal', value: '' })
            }
          }}
        >
          <SelectTrigger className="w-[120px] bg-white/5 border-gray-700/50">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="variable">Variable</SelectItem>
            <SelectItem value="literal">Literal</SelectItem>
          </SelectContent>
        </Select>

        {/* Value input */}
        {kind === 'variable' ? (
          <Input
            className="flex-1 bg-white/5 border-gray-700/50 focus:border-indigo-500/50"
            placeholder="node_name.output.field_name"
            value={value.ref}
            onChange={(e) => onChange({ kind: 'variable', ref: e.target.value })}
          />
        ) : (
          <Input
            className="flex-1 bg-white/5 border-gray-700/50 focus:border-indigo-500/50"
            placeholder="value"
            value={value.value}
            onChange={(e) =>
              onChange({ kind: 'literal', value: e.target.value })
            }
          />
        )}
      </div>

      {/* Quick-pick for variable refs from upstream nodes */}
      {kind === 'variable' && upstreamNodes.length > 0 && (
        <div className="flex flex-wrap gap-1 pt-1">
          {upstreamNodes.flatMap((node) =>
            node.output_schema.map((field) => {
              const ref = `${node.label}.${field.name}`
              return (
                <button
                  key={`${node.id}.${field.name}`}
                  type="button"
                  className="rounded-md border border-gray-700/50 bg-white/5 px-2 py-0.5 text-xs text-muted-foreground hover:bg-white/10 transition-colors"
                  onClick={() => onChange({ kind: 'variable', ref })}
                >
                  {ref}
                </button>
              )
            })
          )}
        </div>
      )}
    </div>
  )
}

export function ConditionBuilder({
  value,
  onChange,
  upstreamNodes,
}: ConditionBuilderProps) {
  const { t } = useTranslation('workflows')

  const condition = value ?? DEFAULT_CONDITION
  const isUnary = UNARY_OPERATORS.includes(condition.operator)

  const handleOperatorChange = (op: string) => {
    const newOp = op as ConditionOperator
    const newIsUnary = UNARY_OPERATORS.includes(newOp)

    if (newIsUnary) {
      // Clear right operand for unary operators
      onChange({
        operator: newOp,
        left: condition.left,
        right: undefined,
      })
    } else {
      // Ensure right operand exists for binary operators
      onChange({
        operator: newOp,
        left: condition.left,
        right: condition.right ?? DEFAULT_OPERAND,
      })
    }
  }

  return (
    <div className="space-y-4">
      {/* Operator */}
      <div className="space-y-2">
        <Label>Operator</Label>
        <Select value={condition.operator} onValueChange={handleOperatorChange}>
          <SelectTrigger className="bg-white/5 border-gray-700/50">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {ALL_OPERATORS.map((op) => (
              <SelectItem key={op} value={op}>
                {t(`flow.conditionOperators.${op}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Left operand */}
      <OperandEditor
        value={condition.left}
        onChange={(left) => onChange({ ...condition, left })}
        label="Left operand"
        upstreamNodes={upstreamNodes}
      />

      {/* Right operand (hidden for unary operators) */}
      {!isUnary && (
        <OperandEditor
          value={condition.right ?? DEFAULT_OPERAND}
          onChange={(right) => onChange({ ...condition, right })}
          label="Right operand"
          upstreamNodes={upstreamNodes}
        />
      )}
    </div>
  )
}
